import { FC, memo, useState } from "react";
import clsx from "clsx";
import styles from "./ManageHackathonBanner.module.css";

import { useParams } from "react-router-dom";
import { useHackathon } from "api/hackathon";
import { routesEnum } from "pages/Routes";
import _BitcoinBanner from "./assets/bitcoin-banner.png";

import { usePublishCondition } from "hooks/usePublishCondition";
import { Hidden } from "components/Hidden";
import { Button } from "components/Button";

import { PublishHackathonModal } from "components/Hackathon";
import { Space, Tooltip } from "antd";
import CopyTextInput from "pages/EditProjectPage/components/CopyTextInput";
import { getHackathonDomain } from "utils/domain";
import { Tag } from "components/Tag";
import { Gift, OngoingTwoTone, Twitch } from "assets/svgs";

const colors = {
  draft: "#F31D1D",
  review: "#00a3ff",
  rejected: "#F31D1D",
  pending: "#fcc300",
  publish: "#67c771",
};

const PrivateLink: FC = () => {
  const { id } = useParams<{ id: string }>();
  const { data } = useHackathon(id, { enabled: false });
  return (
    <div className={styles.privateLinkWrapper}>
      <Space className="mb-8">
        <div className="white text-14 mr-8 bold">Private link</div>
        <CopyTextInput
          text={`${getHackathonDomain(data?.essentials.url)}${
            routesEnum.hackathon
          }/overview/${id}?preview_token=${data?.preview_token}`}
        />
      </Space>
      <div>
        <small>
          <i className="white">
            Use this link to let others preview your hackathon site before you
            publish it. Once you click the link, a cookie will be stored in your
            browser — this will allow you to see the site at its
            &quot;live&quot; url.
          </i>
        </small>
      </div>
    </div>
  );
};

const ManageHackathonBanner: FC = () => {
  const [openPublishModal, setOpenPublishModal] = useState(false);
  const { id } = useParams<{ id: string }>();
  const { data: hackathon } = useHackathon(id, { enabled: false });
  const { canPublish, isPassAll } = usePublishCondition(hackathon);

  return (
    <div className={clsx(styles.root)}>
      <Hidden js cond={hackathon?.status === "draft"}>
        <PrivateLink />
      </Hidden>
      <Hidden cond={!isPassAll}>
        <div className={styles.dotRequire}>
          <div>Item with a red dot still need work</div>
        </div>
      </Hidden>
      <div className={clsx(styles.container, "container-lg")}>
        <Space align="start">
          <h1 className={styles.hackathonName}>{hackathon?.essentials.name}</h1>
          <Tag
            rounded={false}
            type="colored"
            className={styles.statusTag}
            color={colors[hackathon?.status || "draft"]}
          >
            {hackathon?.status || "draft"}
          </Tag>
        </Space>
        <Space>
          <Tooltip
            title={
              Boolean(hackathon?.essentials?.tagLine) === false
                ? "You have to input all information of Essential section before preview the draft site."
                : ""
            }
          >
            <Button
              className={styles.btn}
              disabled={Boolean(hackathon?.essentials?.tagLine) === false}
              target="_blank"
              rel="noopener noreferrer"
              href={`${getHackathonDomain(hackathon?.essentials?.url)}${
                routesEnum.hackathon
              }/overview/${id}`}
            >
              <Space align="baseline" size={5.5}>
                <span className="mr-8">
                  {hackathon?.status === "draft"
                    ? "Preview draft site"
                    : "Live site"}
                </span>
                <Twitch />
              </Space>
            </Button>
          </Tooltip>

          <Hidden cond={hackathon?.status !== "publish"}>
            <Button
              className={styles.btn}
              disabled={!canPublish}
              onClick={() => setOpenPublishModal(true)}
            >
              <Space size={5.5}>
                <span>
                  {hackathon?.status === "draft"
                    ? "Send to review"
                    : hackathon?.status === "rejected"
                    ? "Resend to admin"
                    : hackathon?.status === "review"
                    ? "Reviewing"
                    : hackathon?.status}
                </span>
                {hackathon?.status === "review" ? <OngoingTwoTone /> : <Gift />}
              </Space>
            </Button>
          </Hidden>
        </Space>
      </div>

      {openPublishModal && (
        <PublishHackathonModal
          onClose={() => setOpenPublishModal(!openPublishModal)}
          open={openPublishModal}
        />
      )}

      <div className={styles.backdrop} />
    </div>
  );
};

export default memo(ManageHackathonBanner);
